import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADVANCED_SEARCH_DROPDOWN_CONSTANTS as ASD_Constant } from './AdvanceSearch.constant';
import AdvancedSearchDropdown from './AdvancedSearchDropdown';
import {
  checkHCO,
  checkMultiTin,
  checkSingleTin,
  checkGeoFlow,
  checkIsInternal,
  checkIsSimulationMode,
  isHighVolumeTinFlow,
  checkContractIdFlow,
  isHiVolumeContractIdFlow,
} from '@src/utility/createStorage';
import * as ASDActions from '@src/middleware/actions/AdvanceSearch/ASDActions';
import { RootState } from '@src/store/reduxStore';
import {
  OptionContainer,
  OptionLabel,
  OptionContent,
  OptionSubtitle,
} from './AdvancedSearchDropdown.style';
import { preloadCommonModules } from '@src/utility/moduleLoader';

type currentFlow = 'HCO' | 'TIN' | 'GEO' | 'BULK' | 'CONTRACT' | 'BULKCONTRACT';

interface SearchPayload {
  currentFlow: currentFlow;
  searchVal: string;
}

interface AdvancedSearchOption {
  value: string;
  label: string;
  subtitle: string;
  routePath: string;
}

// Helper function to check if any flow is detected
const checkCurrentFlow = () => {
  // Implement your flow check logic here
  if (checkGeoFlow()) return true;
  else if (sessionStorage.getItem('providerInfo')) return true;
  else if (isHighVolumeTinFlow()) return true;
  else if (checkSingleTin() || checkMultiTin()) return true;
  else if (checkContractIdFlow()) return true;
  else if (isHiVolumeContractIdFlow()) return true;
  return false;
};

// Helper function to check if dropdown should be hidden on current route
const shouldHideDropdown = (pathname: string): boolean => {
  const hiddenFlag = ASD_Constant.HIDDEN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + '/')
  );

  return hiddenFlag || !checkCurrentFlow();
};
const shouldHideForReportingView = (): boolean => {
  const activeView = sessionStorage.getItem('selectedProviderPerformance');
  if (!activeView) return false;
  return ASD_Constant.HIDDEN_VIEWS.some((view) => view === activeView);
};
const AdvancedSearchDropdownConnected = () => {
  const dispatch = useDispatch();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const defaultPlaceholder = ASD_Constant.PLACEHOLDERS.HCO.placeholder;
  const defaultValue = ASD_Constant.NO_OPTIONS.DEFAULT;
  const [searchValue, setSearchValue] = useState('');
  const [placeholder, setPlaceholder] = useState<string>(defaultPlaceholder);
  const [noOptionsMessage, setNoOptionsMessage] = useState<string>(defaultValue);
  const [options, setOptions] = useState<AdvancedSearchOption[]>([]);
  const [inputKey, setInputKey] = useState(0);
  const [aiAssist, setAiAssist] = useState(false);

  const advanceSearchLoading = useSelector(
    (state: RootState) => state?.advanceSearchReducer.advanceSearchLoading
  );
  const advanceSearchData = useSelector(
    (state: RootState) => state?.advanceSearchReducer.advanceSearchData
  );

  // Listen for route changes (works with Angular routing)
  useEffect(() => {
    preloadCommonModules().catch((error) => {
      console.error('Error preloading common modules:', error);
    });

    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for browser navigation (back/forward buttons)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for programmatic navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      handleLocationChange();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  const checkFlow = () => {
    // Implement your flow check logic here
    if (checkGeoFlow()) return 'GEO';
    else if (isHiVolumeContractIdFlow()) return 'BULKCONTRACT';
    else if (checkContractIdFlow()) return 'CONTRACT';
    else if (checkHCO()) return 'HCO';
    else if (isHighVolumeTinFlow()) return 'BULK';
    else if (checkSingleTin() || checkMultiTin()) return 'TIN';
    return 'HCO';
  };

  const generateOptions = (optionData) => {
    if (optionData.length === 0) {
      setNoOptionsMessage(
        searchValue.trim().length >= 3
          ? ASD_Constant.NO_OPTIONS.NOT_FOUND
          : ASD_Constant.NO_OPTIONS.DEFAULT
      );
      setOptions([]);
    } else {
      const formattedOptions = optionData.map((item) => ({
        value: item?.Metric,
        label: item?.Metric,
        subtitle: `${item?.Category}`,
        routePath: item?.RoutePath || '',
      }));
      setOptions(formattedOptions);
    }
  };

  useEffect(() => {
    if (!advanceSearchLoading) {
      // Syncing options with the async redux fetch result is the sync boundary here.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      generateOptions(advanceSearchData);
    }
    // generateOptions intentionally excluded: it should only re-run on new fetch results, not on every searchValue keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSearchData, advanceSearchLoading]);

  // to reset dropdown state when Navigation Flow like HCO , TIN , GEO is changed
  const resetState = () => {
    setSearchValue('');
    setOptions([]);
    setNoOptionsMessage(defaultValue);
    setPlaceholder(defaultPlaceholder);
  };

  const handleSearch = (data) => {
    // dispatch event to other component with selected option data
    resetState();
    setInputKey((k) => k + 1);
    window.location.href = data?.routePath || '/overview-page-advocate'; // navigate to respective page on option select, you can replace this with your routing logic
  };

  // when close icon is clicked on input box
  const onClear = () => {
    resetState();
  };

  const handleInputChange = (inputValue: string, _isArrowKeyDown: boolean) => {
    setSearchValue(inputValue);
    if (inputValue.length < 3) {
      setNoOptionsMessage(defaultValue);
    }
  };

  const handleApiFilter = async (currentValue, _selectedValue) => {
    const currentFlow = checkFlow();
    const payload: SearchPayload = {
      currentFlow,
      searchVal: currentValue.trim(),
    };
    if (currentValue && currentValue.trim().length >= 3) {
      const action = ASDActions.fetchASDOptions(payload);
      dispatch(action);
    }
    return;
  };

  const renderOption = (optionItem) => (
    <OptionContainer>
      <OptionContent>
        <OptionLabel>{optionItem.label}</OptionLabel>
        <OptionSubtitle>{optionItem.subtitle}</OptionSubtitle>
      </OptionContent>
    </OptionContainer>
  );

  const handleAIAssistToggle = (event) => {
    const isChecked = event.target.checked;
    setAiAssist(isChecked);
    if (!isChecked) {
      // when toggled off reset the dropdown to default state
      resetState();
      setInputKey((k) => k + 1); // to reset the SearchInput component
    } else {
      // when toggled on , you can choose to set a different placeholder or options or state as per your requirement
      setPlaceholder('AI Assist Enabled - Type to get AI suggestions');
    }
  };

  const advancedSearchDropdownProps = {
    placeholder,
    noOptionsMessage,
    searchLoading: advanceSearchLoading,
    options,
    inputKey,
    aiAssist,
    width: '440px',
    height: '38px',
    searchValue,
    onClear,
    renderOption,
    handleSearch,
    handleApiFilter,
    handleInputChange,
    handleAIAssistToggle,
  };

  // Check if dropdown should be hidden on current route (after all hooks)
  if (
    shouldHideDropdown(currentPath) ||
    !checkIsInternal() ||
    checkIsSimulationMode() ||
    shouldHideForReportingView()
  ) {
    return <></>;
  }

  return <AdvancedSearchDropdown {...advancedSearchDropdownProps} />;
};

export default AdvancedSearchDropdownConnected;
